import { parseDateTime } from '@agroone/dates'
import { Factory } from './factory'
import { FeatureCollection } from './feature-collection'
import { Intervention } from './intervention'
import { Location } from './location'
import { Offer } from './offer'
import { Recommendation } from './recommendation'
import { Sample } from './sample'
import { SaveFeatureCollection } from './save-feature-collection'
import { Scouting } from './scouting'

export class PlantingContractorArea {
  contractorId!: number
  areaPlanted!: number
}

export class Planting {
  plantingId: string
  seedingRate: number
  seedingRateUnit: string
  germinationDate?: string | null
  weatherConditions?: string
  soilConditions?: string
  plantingNote?: string
  seedLines?: number
  beds?: number
  bedWidth?: number
  bedWidthUnit?: string
  plantingValidated?: boolean
  seedSpacing?: number
  seedSpacingUnit?: string
  initialPlannedHarvestDate?: string | null
  lotNumber?: string
  depthOfPlanting?: number
  depthOfPlantingUnits?: string
  fieldBuffer?: string
  rolledAfterSeed?: boolean
  specificationId?: number
  specificationName?: string
  sectorId?: number
  pivotArea?: number
  contractorAreas?: PlantingContractorArea[]

  // test modification
  constructor() {
    this.plantingId = ''
    this.seedingRate = 0
    this.seedingRateUnit = ''
    this.germinationDate = ''
    this.weatherConditions = ''
    this.soilConditions = ''
    this.plantingNote = ''
    this.seedLines = 0
    this.beds = 0
    this.bedWidth = 0
    this.bedWidthUnit = ''
    this.plantingValidated = false
    this.seedSpacing = 0
    this.seedSpacingUnit = ''
    this.initialPlannedHarvestDate = ''
    this.lotNumber = ''
  }
}

export class PreClosingCancelEntry {
  cause?: string
  area?: number
  unit?: string
}

export class PreClosingTrialEntry {
  cause?: string
  area?: number
  unit?: string
}

export class PreClosing {
  harvestEndDate?: string | null
  mixedSite?: string | null
  cancelEntries?: PreClosingCancelEntry[] | null
  trialEntries?: PreClosingTrialEntry[] | null
  irrigated?: boolean
  note?: string | null
}

export class Crop {
  static readonly className: string = 'Crop'

  id?: number
  supplierReference?: string | null
  plantedDate?: string | null
  plannedHarvestDate?: string | null
  plantedArea: number
  intendedArea: number
  areaUnit: string
  varietyId?: number
  varietyName?: string | null
  processingType?: string | null
  cropType: string
  farmId: number
  farmName: string
  fieldName: string
  fieldId: number | null
  fieldArea: number
  fieldAreaUnit: string
  fieldOrganic: boolean
  startDate?: string | null
  campaign: string
  endDate?: string | null
  region: string
  regionId: number | undefined
  countryId?: number | null
  country: string | null
  town?: string | null
  zipCode?: string | null
  location?: Location | null
  boundary?: FeatureCollection | SaveFeatureCollection | null
  boundaryId?: number | null
  planting?: Planting
  preClosing?: PreClosing
  forecastHarvestDate?: string | null
  phytoValidationDate?: string | null
  growingSystem?: string | null
  associatedCrop?: string | null
  notes?: string
  active?: boolean
  userId?: number
  updatedAt?: string | null
  technician?: number | null
  technicianFirstname?: string
  technicianLastname?: string
  floweringScoutingId?: string
  yieldEstimationScoutingId?: string
  growthStageScoutingId?: number | null
  lastScoutingDate?: string | null
  externalId?: string
  externalClientId?: number
  destinationFactory?: Factory | null
  destinationFactoryId?: number | null
  reentryDate?: string | null
  campaignId: number | undefined
  cropTypeId?: number | null
  cropName?: string
  shortName?: string
  lastFloweringDate?: string | null
  // This property is filled only when the crops are included in the response body of the fields.
  foreignMaterialScoutings?: Scouting[]
  interventions?: Intervention[]
  recommendations?: Recommendation[]
  scoutings?: Scouting[]
  samples?: Sample[]
  offers: Offer[] | null
  lastTimeCropWasGrown?: number | null
  productsUsedLastYear: number[]
  productsUsedTwoYearsAgo: number[]
  growsLastYear: CropGrowPreviousYear[]
  growsTwoYearsAgo: CropGrowPreviousYear[]
  problematicWeeds: string[]
  nutTrees: boolean
  isVirtual: boolean
  isFromVirtualCrop?: boolean
  virtualCropId?: number | null
  relatedCrops?: VirtualRelatedCrop[]
  tillageCondition?: string | null
  varietalType?: string | null
  varietalTypeId?: number | null
  cooperativeId?: number | null
  cooperativeName?: string | null

  constructor(crop?: Crop) {
    this.id = crop?.id
    this.supplierReference = crop?.supplierReference ?? null
    if (crop?.cropName) {
      this.cropName = crop.cropName
    }
    if (crop?.shortName) {
      this.shortName = crop.shortName
    }
    // This property if filled only when the crops are included in the response body of the fields.

    this.foreignMaterialScoutings = crop?.foreignMaterialScoutings || []
    this.interventions = crop?.interventions || []
    this.recommendations = crop?.recommendations || []
    this.scoutings = crop?.scoutings || []
    this.samples = crop?.samples || []
    this.offers = crop?.offers || null
    this.plantedDate = crop?.plantedDate
    this.plannedHarvestDate = crop?.plannedHarvestDate
    this.plantedArea = crop?.plantedArea ?? 0
    this.intendedArea = crop?.intendedArea ?? 0
    this.areaUnit = crop?.areaUnit ?? ''
    this.varietyId = crop?.varietyId
    this.varietyName = crop?.varietyName
    this.farmId = crop?.farmId ?? 0
    this.farmName = crop?.farmName ?? ''
    this.fieldName = crop?.fieldName ?? ''
    this.fieldId = crop?.fieldId ?? null
    this.fieldArea = crop?.fieldArea ?? 0
    this.fieldAreaUnit = crop?.fieldAreaUnit ?? ''
    this.fieldOrganic = crop?.fieldOrganic ?? false
    this.startDate = crop?.startDate ?? null
    this.campaign = crop?.campaign ?? ''
    this.endDate = crop?.endDate
    this.growingSystem = crop?.growingSystem
    this.associatedCrop = crop?.associatedCrop
    this.cropType = crop?.cropType ?? ''
    this.cropTypeId = crop?.cropTypeId
    this.boundaryId = crop?.boundaryId ?? null
    this.region = crop?.region ?? ''
    this.regionId = crop?.regionId
    this.countryId = crop?.countryId ?? null
    this.country = crop?.country ?? ''
    this.town = crop?.town ?? null
    this.zipCode = crop?.zipCode ?? null
    this.forecastHarvestDate = crop?.forecastHarvestDate
    this.phytoValidationDate = crop?.phytoValidationDate
    this.notes = crop?.notes
    this.technician = crop?.technician ?? null
    this.technicianFirstname = crop?.technicianFirstname
    this.technicianLastname = crop?.technicianLastname
    this.lastFloweringDate = crop?.lastFloweringDate
    this.active = !!crop?.active
    this.updatedAt = crop?.updatedAt
    this.userId = crop?.userId
    this.floweringScoutingId = crop?.floweringScoutingId
    this.yieldEstimationScoutingId = crop?.yieldEstimationScoutingId
    this.growthStageScoutingId = crop?.growthStageScoutingId
    this.lastScoutingDate = crop?.lastScoutingDate
    this.destinationFactory = crop?.destinationFactory ?? null
    this.destinationFactoryId = crop?.destinationFactoryId ?? null
    this.reentryDate = crop?.reentryDate ?? null
    this.campaignId = crop?.campaignId
    this.externalId = crop?.externalId
    this.externalClientId = crop?.externalClientId
    this.lastTimeCropWasGrown = crop?.lastTimeCropWasGrown ?? null
    this.productsUsedLastYear = crop?.productsUsedLastYear ?? []
    this.productsUsedTwoYearsAgo = crop?.productsUsedTwoYearsAgo ?? []
    this.growsLastYear = crop?.growsLastYear ?? []
    this.growsTwoYearsAgo = crop?.growsTwoYearsAgo ?? []
    this.nutTrees = crop?.nutTrees ?? false
    this.isVirtual = false
    this.isFromVirtualCrop = crop?.isFromVirtualCrop ?? false
    this.virtualCropId = crop?.virtualCropId ?? null
    this.problematicWeeds = crop?.problematicWeeds ?? []
    this.relatedCrops = crop?.relatedCrops ?? []
    this.tillageCondition = crop?.tillageCondition ?? null
    this.processingType = crop?.processingType ?? null
    this.varietalType = crop?.varietalType ?? null
    this.varietalTypeId = crop?.varietalTypeId ?? null
    this.cooperativeId = crop?.cooperativeId ?? null
    this.cooperativeName = crop?.cooperativeName ?? null

    if (crop?.planting) {
      this.planting = crop.planting
    }
    if (crop?.preClosing) {
      this.preClosing = crop.preClosing
    }
    if (crop?.location) {
      this.location = crop.location
    }
    if (crop?.boundary) {
      this.boundary = crop.boundary
    }
  }

  get plantedDateAsDate(): Date | undefined {
    return parseDateTime(this.plantedDate)
  }

  get forecastHarvestDateAsDate(): Date | undefined {
    return parseDateTime(this.forecastHarvestDate)
  }

  get reentryDateAsDate(): Date | null | undefined {
    return this.reentryDate ? parseDateTime(this.reentryDate) : null
  }
}

export class CropGrowPreviousYear {
  constantKeyId!: number
  area!: number
  unit!: string
}

export class CropPlantingContractorAreaDAO {
  id?: number
  plantingId?: number
  contractorId?: number
  areaPlanted?: number
  createdAt?: string
  updatedAt?: string
}

export class CropBoundary {
  id: number | undefined
  cropName: string
  boundary?: FeatureCollection | null

  constructor(cropBoundary?: CropBoundary) {
    this.id = cropBoundary?.id
    this.cropName = cropBoundary?.cropName ?? ''
    this.boundary = cropBoundary?.boundary ?? null
  }
}

export class VirtualRelatedCrop {
  id!: number
  area!: number
  areaUnit!: string
  name!: string
  active!: boolean
}

/**
 * Specific class that is used to define if yes or no a crop is displayed at the bottom of the offer crop list and if it's greyed out
 */
export class AgropilotCrop extends Crop {
  isFullyHarvestedCrop!: boolean
}
